import { z } from 'zod'
import { isoDay, localTime, someText, uuid } from './primitives.ts'

/** The course notices (M16-B1, docs/M16.md "B1"; benchmark 9 and 38, GK-036): a short note whoever runs the course writes for the team — a green
 *  closed, carts on the paths, aeration on the Bleu this week, the day's pins, a competition with its first start — seen at once on Today, on the
 *  shed screen, over the day board and the week grid for its dates, and printed for the reception desk. Nothing here leaves the building: what
 *  the clubhouse may read through a link is B2's (release b, after the owner's answer). Each schema mirrors `core.command_type.payload_schema`
 *  in `20261201100100_course_notices.sql`; the contract check (ops/contracts/check.mjs) runs the same fixtures through both. */
export const NOTICE_KINDS = ['closure', 'restriction', 'maintenance', 'info', 'pins', 'event'] as const
export type NoticeKind = (typeof NOTICE_KINDS)[number]
/** The server's rule on each language of the text, in code points (NOTICE_TEXT_TOO_LONG): the schema admits a text, the handler and the column hold the length,
 *  so the refusal carries its own message; the form caps its fields here. */
export const NOTICE_TEXT_MAX = 600
/** How many areas one notice may name (a loop is one area: "the Rouge" is one id). */
export const NOTICE_MAX_AREAS = 60

const noticeAreas = z.array(uuid).max(NOTICE_MAX_AREAS)

/** A new notice, live at once (intent, course_status.manage). */
export const courseNoticeWriteV1 = z.strictObject({
  notice_id: uuid, kind: z.enum(NOTICE_KINDS), text: someText, area_ids: noticeAreas, starts_on: isoDay, ends_on: isoDay, start_time: localTime.optional(),
})
/** What changed, from the revision the editor read; the status and the approval are never in `set`. */
export const courseNoticeUpdateV1 = z.strictObject({
  notice_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({
    kind: z.enum(NOTICE_KINDS).optional(), text: someText.optional(), area_ids: noticeAreas.optional(), starts_on: isoDay.optional(), ends_on: isoDay.optional(), start_time: localTime.optional(),
  }).refine((o) => Object.keys(o).length >= 1),
})
/** The notice leaves every list (a repeat is silent). */
export const courseNoticeRetireV1 = z.strictObject({ notice_id: uuid })
/** "May leave the building", written once (course_status.publish; read by B2 only; a repeat is silent). */
export const courseNoticeApproveV1 = z.strictObject({ notice_id: uuid })

export type CourseNoticeWriteV1 = z.infer<typeof courseNoticeWriteV1>
export type CourseNoticeUpdateV1 = z.infer<typeof courseNoticeUpdateV1>
export type CourseNoticeRetireV1 = z.infer<typeof courseNoticeRetireV1>
export type CourseNoticeApproveV1 = z.infer<typeof courseNoticeApproveV1>
